import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Tag, Tooltip } from '~/components';
import { Avatar } from '~/components/Avatar';
import { IconButton } from '~/components/IconButton';
import { Popover } from '~/components/Popover';
import { cn } from '~/lib/cn';
import { ArtistContactInfo } from '~/modules/artist-roster/components/ArtistContactInfo/ArtistContactInfo';
import { useIsContactInfoEnabled } from '~/modules/artist-roster/hooks/useIsContactInfoEnabled/useIsContactInfoEnabled';
import { LabelsCell } from '~/modules/artist-roster/pages/roster/components/ArtistsTable/LabelsCell';
import type {
  ArtistTableMetaType,
  ArtistType,
} from '~/modules/artist-roster/types';
import { useUser } from '~/modules/auth/hooks/useUser';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { doLabelsHavePermission } from '../utils/doLabelsHavePermission';

const AVATAR_CLASS_NAME = cn(
  'size-[2.875rem] outline outline-2 bg-emphasis-subtle outline-emphasis-subtle',
);

export function useArtistColumns() {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const columnHelper = useMemo(() => createColumnHelper<ArtistType>(), []);
  const { enabled: isContactInfoEnabled } = useIsContactInfoEnabled();

  const { permissions } = useUser();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        id: 'name',
        header: () => t('artistTable.columns.name'),
        cell: ({ row }) => (
          <div className="flex items-center gap-md">
            {row.original.image ? (
              <img
                src={row.original.image}
                alt={row.original.name}
                className={cn('rounded-full object-cover', AVATAR_CLASS_NAME)}
                loading="lazy"
              />
            ) : (
              <Avatar
                name={row.original.name}
                variant="base"
                className={AVATAR_CLASS_NAME}
              />
            )}
            <div className="line-clamp-2 text-headline-sm font-bold leading-tight group-hover/link:underline">
              {row.original.name}
            </div>
            {/* @TODO WMG1-572 - Put back in once back end data is fixed. */}
            {/* {row.original.isCrossover ? <ArtistCrossoverIcon /> : null} */}
          </div>
        ),
      }),
      columnHelper.accessor('labels', {
        id: 'labels',
        header: () => t('artistTable.columns.labels'),
        cell: ({ row }) => <LabelsCell artist={row.original} />,
      }),
      columnHelper.accessor('reports', {
        id: 'reports',
        header: () => t('artistTable.columns.dataTools'),
        cell: ({ row, table }) => {
          return (
            <div className="flex flex-row gap-sm">
              {row.original?.reports.map((q) => {
                return (
                  <a
                    key={q.name}
                    href={q.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();

                      const meta = table.options?.meta as
                        | ArtistTableMetaType
                        | undefined;

                      trackEvent('rosterTableArtistInteraction', {
                        artist_id: row.original.partyId,
                        artist_name: row.original.name,
                        click_type: q.name,
                        roster_label_filters_count: meta?.filtersCount ?? 0,
                        roster_crossover_filter_applied:
                          meta?.crossoverFilterApplied ?? false,
                        roster_label_filters_applied:
                          meta?.filtersApplied ?? false,
                        roster_status: meta?.rosterStatus ?? 'all',
                      });
                    }}
                  >
                    <Tag variant="secondary">
                      {q.displayName ?? q.name}
                      <Icon
                        type="external-link"
                        size="sm"
                        variant="light"
                        role="presentation"
                        aria-hidden
                        aria-label="external-url-icon"
                        className="-mr-px text-white"
                      />
                    </Tag>
                  </a>
                );
              })}
            </div>
          );
        },
      }),
      columnHelper.accessor('socials', {
        id: 'socials',
        header: () => t('artistTable.columns.socials'),
        meta: {
          className: 'w-[16%]',
        },
        cell: ({ row, table }) => {
          return (
            <div className="-ml-md flex items-center">
              {row.original?.socials?.map((s) => {
                return (
                  <IconButton
                    key={`${s.name}_${s.url}`}
                    iconType={s.name}
                    className="p-lg leading-none duration-300 hover:bg-emphasis-strong"
                    iconVariant="brands"
                    size="medium"
                    asLink={true}
                    linkTo={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    onClick={(e) => {
                      e.stopPropagation();

                      const meta = table.options?.meta as
                        | ArtistTableMetaType
                        | undefined;

                      trackEvent('rosterTableArtistInteraction', {
                        artist_id: row.original.partyId,
                        artist_name: row.original.name,
                        click_type: s.name,
                        roster_label_filters_count: meta?.filtersCount ?? 0,
                        roster_crossover_filter_applied:
                          meta?.crossoverFilterApplied ?? false,
                        roster_label_filters_applied:
                          meta?.filtersApplied ?? false,
                        roster_status: meta?.rosterStatus ?? 'all',
                      });
                    }}
                  />
                );
              })}
            </div>
          );
        },
      }),
      ...(isContactInfoEnabled
        ? [
            columnHelper.display({
              id: 'contactInfo',
              enableSorting: false,
              header: () => null,
              meta: { className: 'w-3xl' },
              cell: ({ row }) => {
                const hasContactInfoPermission = doLabelsHavePermission(
                  row.original.labels,
                  permissions,
                  'PARTY_CONTACT_INFO.VIEW',
                );

                if (!hasContactInfoPermission) {
                  return null;
                }

                const { entityType } = row.original;
                const handleOpenChange = (open: boolean) => {
                  if (open) {
                    trackEvent('contactInfoViewed', {
                      source: 'Roster',
                      entity_type: entityType,
                    });
                  }
                };

                return (
                  <Popover
                    side="left"
                    sideOffset={6}
                    align="start"
                    content={
                      <ArtistContactInfo
                        partyId={row.original.partyId}
                        entityType={entityType}
                      />
                    }
                    stopPropagation
                    triggerAsChild
                    onOpenChange={handleOpenChange}
                  >
                    <button
                      aria-label={t('labels.contactInfo')}
                      className="rounded-full border p-sm transition-colors border-transparent hover:bg-emphasis-subtle hover:border-emphasis-strong"
                    >
                      <Tooltip
                        content={t('labels.contactInfo')}
                        delayDuration={0}
                        sideOffset={12}
                        side="top"
                        size="sm"
                        triggerAsChild
                      >
                        <div className="flex size-md items-center justify-center">
                          <Icon
                            type="ellipsis-vertical"
                            variant="regular"
                            size="sm"
                          />
                        </div>
                      </Tooltip>
                    </button>
                  </Popover>
                );
              },
            }),
          ]
        : []),
    ];
  }, [columnHelper, isContactInfoEnabled, t, trackEvent, permissions]);

  return { columns };
}
